import React from "react";
import { useStyles } from "./styles";
import { AgGridReact } from "ag-grid-react-32";
import { ColDef, GridApi, GridReadyEvent, RowSelectionOptions, SortState } from "ag-grid-community-32";
import { formatDateRange } from "../helpers/formatDateRange";
import moment from "moment";
import { ListTableSelect, IListTableHandlers } from "./list-grid-select";
import { ListTableFilters } from "./list-grid-filters";
import { DATATEST } from "constants/integration";
import { PROMOTIONS_LIST_KEY } from "..";
import { IStore } from "reducers";
import { connect } from "react-redux";
import type { PriceEntity } from "../../entity/price-entity/promotions.types";
import { CircularProgress } from "material-ui";
import type { IPromotionsListFilters } from "../interfaces";
import { setPromotionsSort } from "actions/promotions";

export interface IStateProps {
    promotions: {
        results: PriceEntity[];
        hasMoreResults: boolean;
        nextPage: string;
        isFetching: boolean;
        filters: IPromotionsListFilters;
        filteredResults: PriceEntity[];
        sort: SortState;
        shouldPersist: boolean;
    };
}

export interface IActionProps {
    setSort(sort: SortState): void;
}

export interface IPromotionGrid {
    singleSelectedRowActionHandlers: IListTableHandlers[];
    multiSelectedRowActionHandlers: IListTableHandlers[];
    isLoading: boolean;
    onFilterChange(filters: IPromotionsListFilters): void;
}

const PromotionGridComponent: React.FC<IStateProps & IActionProps & IPromotionGrid> = ({
    promotions = {
        results: [],
        isFetching: true,
        filters: {},
        filteredResults: [],
        sort: { sortModel: [] },
        shouldPersist: false,
    },
    setSort,
    singleSelectedRowActionHandlers,
    multiSelectedRowActionHandlers,
    isLoading,
    onFilterChange,
}) => {
    const classes = useStyles();

    React.useEffect(() => {
        // Inject CSS for the newer version
        const gridCss = document.createElement("link");
        gridCss.rel = "stylesheet";
        gridCss.href = require("ag-grid-community-32/styles/ag-grid.css");
        document.head.appendChild(gridCss);

        const themeCss = document.createElement("link");
        themeCss.rel = "stylesheet";
        themeCss.href = require("ag-grid-community-32/styles/ag-theme-quartz.css");
        document.head.appendChild(themeCss);

        return () => {
            // Cleanup to avoid style leakage
            document.head.removeChild(gridCss);
            document.head.removeChild(themeCss);
        };
    }, []);

    const entries = React.useMemo(() => {
        const hasFilter =
            Object?.entries(promotions.filters || {})?.filter(([key, value]: [string, string]) => value.length > 0)
                ?.length > 0;
        return hasFilter ? promotions.filteredResults : promotions.results;
    }, [promotions.results, promotions.results?.length, promotions.filteredResults, promotions.filters]);

    React.useEffect(() => {
        if (!!promotions?.filters && Object.keys(promotions?.filters)?.length > 0) {
            onFilterChange(promotions.filters);
        }
    }, [promotions.results]);

    const agGridApiRef = React.useRef<GridApi>(null);

    const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
    const [isSelectedActionBoxOpen, setSelectedActionBox] = React.useState<boolean>(false);
    const [isFiltersOpen, setFilters] = React.useState<boolean>(true);

    const onSelectionChange = React.useCallback(
        (e) => {
            setSelectedIds(e.api.getSelectedNodes().map((e) => e.data.id));
            setSelectedActionBox(e.api.getSelectedNodes().length > 0);
            setFilters(e.api.getSelectedNodes().length === 0);
        },
        [setSelectedIds, setSelectedActionBox, setFilters]
    );

    const onCloseSelectedActionBox = React.useCallback(() => {
        setSelectedIds([]);
        setSelectedActionBox(false);
        setFilters(true);

        agGridApiRef.current!.deselectAll();
    }, [setSelectedIds, setSelectedActionBox, setFilters]);

    const promotionStatus = (e) => {
        return (
            <div className={classes.listRowItem}>
                <div className={e.data.enabled ? classes.active : classes.inactive}>
                    <span data-test={DATATEST.PROMOTIONS.LIST_STATUS} style={{ color: "white" }}>
                        {e.data.enabled ? "Active" : "Inactive"}
                    </span>
                </div>
            </div>
        );
    };

    const promotionName = (e) => {
        return (
            <div>
                <span data-test={DATATEST.PROMOTIONS.LIST_NAME}>{e.data.name}</span>
            </div>
        );
    };

    const rowData = React.useMemo(() => {
        return entries.map((entry) => {
            const { name, enabled, created_by, visible_start_date, visible_end_date, last_updated_date, id, actions } =
                entry;

            const promotionType =
                actions?.[0]?.price_action?.scope === "PRICE_ACTION_SCOPE_TOTAL" ? "Order" : "Product";

            return {
                name,
                id,
                type: promotionType,
                enabled,
                activeFrom: formatDateRange(visible_start_date, visible_end_date),
                activeFromIso: visible_start_date,
                createdBy: created_by,
                lastModified: moment(last_updated_date).format("MMM DD, YYYY"),
                lastModifiedIso: last_updated_date,
            };
        });
    }, [entries]);

    const defaultColDef = React.useMemo(() => {
        return {
            sortable: true,
            cellClass: classes.gridCell,
        };
    }, []);

    const [columnDefs, setColumnDefs] = React.useState<ColDef[]>([
        {
            field: "name",
            flex: 3,
            cellRenderer: promotionName,
            valueGetter: (p) => p.data.name,
            comparator: (valueA, valueB) => {
                if (valueA.toLowerCase() == valueB.toLowerCase()) {
                    return 0;
                }
                return valueA.toLowerCase() > valueB.toLowerCase() ? 1 : -1;
            },
        },
        { field: "type", flex: 1 },
        {
            field: "status",
            cellRenderer: promotionStatus,
            flex: 1,
            valueGetter: (p) => (p.data.enabled ? "active" : "inactive"),
        },
        {
            field: "activeFrom",
            headerName: "Active from",
            valueGetter: (p) => p.data.activeFrom,
            flex: 2,
            comparator: (valueA, valueB, nodeA, nodeB) => {
                if (nodeA.data.activeFromIso == nodeB.data.activeFromIso) {
                    return 0;
                }

                return nodeA.data.activeFromIso > nodeB.data.activeFromIso ? 1 : -1;
            },
        },
        {
            field: "createdBy",
            headerName: "Created by",
            valueGetter: (p) => p.data.createdBy,
            flex: 3,
        },
        {
            field: "lastModified",
            headerName: "Last modified",
            valueGetter: (p) => p.data.lastModified,
            flex: 1.5,
            comparator: (valueA, valueB, nodeA, nodeB) => {
                if (nodeA.data.lastModifiedIso == nodeB.data.lastModifiedIso) {
                    return 0;
                }

                return nodeA.data.lastModifiedIso > nodeB.data.lastModifiedIso ? 1 : -1;
            },
        },
    ]);

    const gridOptions = {
        rowClass: classes.gridRow,
        suppressCellFocus: true,

        loading: true,

        loadingOverlayComponent: CircularProgress,
        loadingOverlayComponentParams: {
            size: 30,
            thickness: 3,
        },
        ...(promotions?.sort?.sortModel?.length > 0 &&
            promotions.shouldPersist && {
                initialState: {
                    columnSizing: {
                        columnSizingModel: [
                            {
                                colId: "name",
                                flex: 3,
                            },
                            {
                                colId: "type",
                                flex: 1,
                            },
                            {
                                colId: "status",
                                flex: 1,
                            },
                            {
                                colId: "activeFrom",
                                flex: 2,
                            },
                            {
                                colId: "createdBy",
                                flex: 3,
                            },
                            {
                                colId: "lastModified",
                                flex: 1.5,
                            },
                        ],
                    },
                    sort: promotions.sort,
                },
            }),
    };

    React.useEffect(() => {
        agGridApiRef?.current?.setGridOption("loading", isLoading);
    }, [isLoading]);

    const selection: RowSelectionOptions = {
        mode: "multiRow",
        enableClickSelection: true,
        enableSelectionWithoutKeys: true,
    };

    const selectedRowActionHandlers = React.useMemo(
        () => (selectedIds.length < 2 ? singleSelectedRowActionHandlers : multiSelectedRowActionHandlers),
        [selectedIds, singleSelectedRowActionHandlers, multiSelectedRowActionHandlers]
    );

    const onGridReady = (api: GridReadyEvent) => {
        agGridApiRef.current = api.api;
    };

    React.useEffect(() => {
        return () => {
            if (agGridApiRef.current?.getState()?.sort) {
                setSort(agGridApiRef.current.getState().sort);
            } else {
                setSort({ sortModel: [] });
            }
        };
    }, []);

    return (
        <div className={classes.wrapper}>
            <ListTableSelect
                isOpen={isSelectedActionBoxOpen}
                onCloseClick={onCloseSelectedActionBox}
                actionHandlers={selectedRowActionHandlers}
                selectedIds={selectedIds}
            />
            <ListTableFilters
                isOpen={isFiltersOpen}
                selectedFilters={promotions.filters}
                allUsers={[...new Set(promotions.results.map((res: { created_by: any }) => res.created_by))]}
                onFilterChange={onFilterChange}
            />
            <div data-test={DATATEST.PROMOTIONS.LIST_LIST} className={`ag-theme-quartz ${classes.agWrapper}`}>
                <AgGridReact
                    onGridReady={onGridReady}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    headerHeight={64}
                    rowHeight={60}
                    rowSelection={selection}
                    onSelectionChanged={onSelectionChange}
                    gridOptions={gridOptions}
                    suppressColumnVirtualisation={true}
                />
            </div>
        </div>
    );
};

const mapStateToProps = (state: IStore): IStateProps => ({
    promotions: state.promotions.collections[PROMOTIONS_LIST_KEY] || {
        results: [],
        isFetching: false,
        hasMoreResults: false,
        nextPage: undefined,
        filters: {},
        shouldPersist: false,
    },
});

const mapDispatchToProps = (dispatch) => ({
    setSort: (sort: SortState) => dispatch(setPromotionsSort(sort)),
});

export default connect<IStateProps, IActionProps, IPromotionGrid>(
    mapStateToProps,
    mapDispatchToProps
)(PromotionGridComponent);
